﻿/**
 * Throws an error if the provided date is invalid.
 * @param date The Date object to validate.
 * @param type The name of the type being constructed (for error messages).
 * @throws Error if the date is invalid (NaN timestamp).
 */
export function throwsIfInvalidDate(date: Date, type: string): void {
	if (!isNaN(date.getTime())) {
		return
	}
	//never toISOString() here — it throws RangeError on the very invalid date being reported
	throw new Error(
		`Invalid date. The ${type} constructor requires a valid date.`
	);
}

/**
 * Converts a UTC date to a local date by creating a new Date with the UTC components interpreted as local time.
 * @param date The UTC Date to convert.
 * @returns A new Date object with the UTC components interpreted as local time.
 */
export function fromUtc(date: Date): Date {
	return createFromNumbers(date.getFullYear(), date.getMonth() + 1, date.getDate(), date.getHours(), date.getMinutes(), date.getSeconds(), date.getMilliseconds());
}

/**
 * Creates a Date object from numeric date and time components in UTC.
 * @param year The full year.
 * @param month The month (1-12). Defaults to 1.
 * @param day The day of the month (1-31). Defaults to 1.
 * @param hours The hours (0-23). Defaults to 0.
 * @param minutes The minutes (0-59). Defaults to 0.
 * @param seconds The seconds (0-59). Defaults to 0.
 * @param milliseconds The milliseconds (0-999). Defaults to 0.
 * @returns A new Date object representing the specified UTC date and time.
 */
export function createFromNumbers(year: number, month: number = 1, day: number = 1, hours: number = 0, minutes: number = 0, seconds: number = 0, milliseconds: number = 0): Date {
	return new Date(Date.UTC(year, month - 1, day, hours, minutes, seconds, milliseconds));
}

/**
 * Returns the wall-clock date/time components of an instant as observed in the given IANA time zone.
 * @param instantMs The instant, in milliseconds since the Unix epoch.
 * @param timeZone An IANA time zone name (e.g. any value from Intl.supportedValuesOf("timeZone")).
 * @returns The year, month (1-12), day, hour (0-23), minute and second in that time zone.
 * @throws RangeError if timeZone is not a valid IANA time zone name.
 */
export function partsInTimeZone(instantMs: number, timeZone: string): {
	year: number; month: number; day: number; hour: number; minute: number; second: number;
} {
	const parts = new Intl.DateTimeFormat("en-US", {
		timeZone, hour12: false,
		year: "numeric", month: "2-digit", day: "2-digit",
		hour: "2-digit", minute: "2-digit", second: "2-digit"
	}).formatToParts(new Date(instantMs));

	const get = (type: Intl.DateTimeFormatPartTypes) =>
		parseInt(parts.find(p => p.type === type)?.value ?? "0", 10);

	// Some engines emit hour "24" for midnight; normalize to 0. (ponytail: Intl quirk)
	const hour = get("hour");
	return {
		year: get("year"), month: get("month"), day: get("day"),
		hour: hour === 24 ? 0 : hour, minute: get("minute"), second: get("second")
	};
}

/**
 * Returns the last day of the month for the specified date.
 * @param value The Date object to get the last day for.
 * @returns The last day of the month as a number (28-31).
 */
export function getLastOfMonth(value: Date): number {
	return new Date(value.getFullYear(), value.getMonth() + 1, 0).getDate();
}

/**
 * Determines whether two dates are equal by comparing their timestamps.
 * @param date1 The first Date to compare.
 * @param date2 The second Date to compare (can be null or undefined).
 * @returns true if both dates represent the same point in time; otherwise, false.
 */
export function dateEquals(date1: Date, date2: Date | null | undefined): boolean {
	if (date2 == null) {
		return false;
	}

	return date1.getTime() === date2.getTime();
}

/**
 * Compares two dates by their timestamps.
 * @param date1 The first Date to compare.
 * @param date2 The second Date to compare (can be null or undefined).
 * @returns A signed number indicating the relative values:
 * - Less than 0 if date1 is earlier than date2
 * - 0 if date1 is equal to date2
 * - Greater than 0 if date1 is later than date2
 * - 1 if date2 is null or undefined
 */
export function dateCompare(date1: Date, date2: Date | null | undefined): number {
	if (date2 == null) {
		return 1;
	}

	return date1.getTime() - date2.getTime();
}
